/* assets/pf-warm.js —— 设计台「加购预热」（2026-10-12）
   目的：把 Buy it now 里最贵的两步（导出 → 上传）**提前**到用户还在画的时候做，
   点下去时若设计没再变，直接复用结果 ⇒ 前端只剩「建商品 + 放进购物车」。
   🔴 2026-10-12 修订：**不再预热建商品**（/catalog/design-listing 限流 5 次/分钟/IP，
      编辑期反复预热会吃光配额 ⇒ 真实点击被 429 拦死）；建商品延到**真实点击**做（一单 1 次）。

   🔴 为什么必须外置：sections/design-canvas.liquid 距 256KB 顶只剩约 1.4KB。
   🔴 为什么不能"取消"这两步：商品身份 = 设计图哈希（handle `custom-<pid>-<designId>`），
      订单上还要写 `_design_url`（Printful 生产/出图靠它）⇒ 是后续步骤的**输入**，只能提前。

   唯一接口（与 __hdDraw / __pfmDraft 同模式）：
     · 依赖注入：section 设 `window.__pfmWarmDeps`
     · 启动：`window.__pfmWarm.init()`
     · 取用：`window.__pfmWarm.take()` —— 命中返回缓存（含 exports/faces/designId/primaryUrl），否则 null

   失效判据用**版本号**（不是重算哈希 —— 那要再导出一遍，等于没省）：
     画布每次 object:added/modified/removed + 切面 ⇒ ver++；预热完成时记下当时的 ver；
     take() 时 ver 不一致（= 用户又动过设计）⇒ 视为失效，走完整链路。
*/
(function () {
  'use strict';

  /* 🔴🔴 2026-10-13：**总开关（唯一）**。用户裁决「预热副作用太大，关闭」——
     预热会在**停笔仅 5s** 就自动执行「导出 + 上传」，代价：
       · 反复占主线程（解码图片 + 编码 PNG，约 150~300ms）⇒ 操作时卡顿
       · 吃跨洋上传流量；反复触及限流配额；画了没买也白传
     收益只是「点 Buy it now 快 ~1.5s」⇒ 判定不划算。
     关闭时：init() **连事件都不绑**（零副作用），take() 恒返回 null
     ⇒ 加购自动回退到**完整链路**（导出 → 上传 → 建商品 → 进购物车）= 预热之前的原行为。
     ⚠️ 恢复只需把下一行的值改回「真」。 */
  var ENABLED = false;

  var D = null, timer = null, cache = null, running = false, pending = false, MUTE = false;
  var DELAY = 5000;   // 停笔 5s 才预热
  /* 2026-10-13：**操作中不预热**。
     原实现只在 object:added/modified/removed 上 bump，而 `object:modified` **只在松手那一刻**触发
     ⇒ 拖动期间（只发 object:moving）不再重置计时器 ⇒ 拖得久一点，预热就会在「手还按着」时启动；
     导出要解码图片 + 编码 PNG（约 150~300ms 主线程）⇒ 拖动卡一下。
     改法：mouse:down / 文字进入编辑 ⇒ BUSY，并**取消已排队**的那次；松手（mouse:up / 编辑退出）
     ⇒ 重新按 DELAY 计时。静止不动时行为与改前完全一致。 */
  var BUSY = false, busyTimer = null;

  function deps() { return D || {}; }
  function call(n, a) { var f = deps()[n]; return (typeof f === 'function') ? f(a) : undefined; }

  function bump() { if (MUTE) return; clearTimeout(timer); timer = setTimeout(run, DELAY); }

  /* 画布指纹（**轻量**：只序列化 + 折叠 hash，不导出 PNG）——
     预热会在导出时来回切面改画布，若用"版本号"判失效会被自身顶高、缓存永远作废（实测）。
     指纹偏保守：任何对象变化都会让它不同 ⇒ 只会"多失效"，不会"错命中"。 */
  function fp() {
    try {
      var j = deps().canvas().toJSON();
      if (j && Array.isArray(j.objects)) j.objects = j.objects.filter(function (o) { return !o.isGrid; });
      var str = String(j.objects ? j.objects.length : 0) + '|' + JSON.stringify(j);
      var h = 5381;
      for (var i = 0; i < str.length; i++) { h = ((h << 5) + h + str.charCodeAt(i)) | 0; }
      return str.length + ':' + (h >>> 0).toString(36);
    } catch (e) { return ''; }
  }

  function run() {
    timer = null;
    if (BUSY) return;                                 /* 手没松开 ⇒ 这次不预热（松手时 setBusy(false) 会重排） */
    if (running) { pending = true; return; }
    running = true;
    prepare().then(done, done);
    function done() { running = false; if (pending) { pending = false; run(); } }
  }

  function prepare() {
    var fpBefore = fp();
    var p;
    MUTE = true;                                  /* 预热自身会改画布 ⇒ 屏蔽它触发的重调度 */
    try { p = deps().exportAllPlacements(); } catch (e) { MUTE = false; return Promise.resolve(); }
    var done = function () {
      MUTE = false;
      if (fp() !== fpBefore) { try { run(); } catch (e) {} }   /* 预热期间用户真改过 ⇒ 立刻重来 */
    };
    return Promise.resolve(p).then(function (exports) {
      if (!exports) return;
      var keys = Object.keys(exports);
      if (!keys.length) return;
      var did = deps().pfmDesignId(exports);
      if (!did) return;
      if (cache && cache.designId === did && cache.fp === fpBefore) return;   // 同一设计、期间没动过
      return Promise.resolve(deps().pfmUploadFaces(exports, did)).then(function (faces) {
        if (!faces) return;
        var primary = (faces[keys[0]] || {}).url;
        if (!primary) return;
        /* 🔴🔴 2026-10-12: 预热**只做导出+上传**，不再调 /catalog/design-listing（建商品）。
           那是 5 次/分钟/IP 的限流端点 —— 编辑期反复预热会把配额吃光，
           真实点击时被 429 拦死（用户报「Add to cart failed」）。
           建商品延到真实点击做（bridge 有 reuse 分支，同设计重复调用是幂等的）。 */
        cache = { designId: did, fp: fpBefore, exports: exports, faces: faces, primaryUrl: primary, ts: Date.now() };
        try { console.log('[warm] ready (export+upload)', did, keys.length + ' face(s)'); } catch (e) {}
      });
    }).then(done, done).catch(function (e) { try { console.warn('[warm] failed:', e && e.message); } catch (_) {} });
  }

  /* 2026-10-13：操作中守卫。on=true 进入操作；on=false 离开操作（= 手松开）。
     兜底：mouse:up 偶尔收不到（手指移出画布 / 切到后台 / 被系统手势吃掉）⇒ 30s 后强制解除，
     避免 BUSY 永久卡住导致预热再也不跑。 */
  function setBusy(on) {
    clearTimeout(busyTimer); busyTimer = null;
    BUSY = !!on;
    if (BUSY) {
      clearTimeout(timer); timer = null;              /* 操作开始 ⇒ 取消已排队的那次预热 */
      busyTimer = setTimeout(function () { busyTimer = null; setBusy(false); }, 30000);
    } else {
      bump();                                         /* 松手 ⇒ 重新按 DELAY 计时 */
    }
  }

  /* 命中判据：存在 + 设计没再变动过。取用后**不清空**（可重复用；设计变了自然失效）。 */
  function take() {
    if (!ENABLED) return null;                     /* 关闭 ⇒ 永不当命中，加购走完整链路 */
    if (!cache) return null;
    if (cache.fp !== fp()) return null;             /* 设计变了（含切面）⇒ 失效 */
    return cache;
  }

  function init() {
    if (!ENABLED) { try { console.log('[warm] disabled — 预热已关闭，加购走完整链路'); } catch (e) {} return; }
    D = window.__pfmWarmDeps || {};
    var c = call('canvas');
    if (c && c.on) {
      c.on('object:added', bump);
      c.on('object:modified', bump);
      c.on('object:removed', bump);
      /* 2026-10-13：操作中守卫 —— 按住（拖动 / 缩放手柄 / 画笔 / 文字编辑）期间不预热。
         fabric 会把触摸合成 mouse:down/mouse:up ⇒ 触摸设备同样生效。 */
      c.on('mouse:down', function () { setBusy(true); });
      c.on('mouse:up', function () { setBusy(false); });
      try { c.on('text:editing:entered', function () { setBusy(true); }); } catch (e) {}
      try { c.on('text:editing:exited', function () { setBusy(false); }); } catch (e) {}
    }
  }

  window.__pfmWarm = { init: init, take: take, bump: bump, busy: function () { return BUSY; }, ready: function () { return !!take(); } };
})();
